import { ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/formatters";
import type { Policy } from "@shared/schema";
import { PolicyStatusBadge } from "./PolicyStatusBadge";
import { PolicySubstatusText } from "./PolicySubstatusText";
import { AorIcon, aorPolicyTooltip, isAorFlagged } from "./aor";
import {
  formatPolicyCancellationReason,
  formatPolicyPremium,
  hasCancellationReason,
  getRawCancellationReason,
  shouldShowCancellationDate,
} from "./policy-status";

interface Props {
  policies: Policy[];
  onSelect: (policy: Policy) => void;
  // CSR-2327 — the AOR experience flag, read once by the insured page.
  showAor?: boolean;
}

export function PoliciesTable({ policies, onSelect, showAor = false }: Props) {
  const headClass = "px-6 py-3 text-xs uppercase tracking-wider text-muted-foreground";
  const dateHeadClass = "px-3 py-3 text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap";
  const cellClass = "px-6 py-5";
  const dateCellClass = "px-3 py-3 whitespace-nowrap text-muted-foreground";

  return (
    <div className="overflow-x-auto" data-testid="policies-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className={`${headClass} whitespace-nowrap`}>Policy Number</TableHead>
            <TableHead className={`${headClass} whitespace-nowrap`}>Type</TableHead>
            <TableHead className={headClass}>Status</TableHead>
            <TableHead className={`${headClass} whitespace-nowrap`}>Carrier</TableHead>
            <TableHead className={headClass}>Premium</TableHead>
            <TableHead className={dateHeadClass}>Effective</TableHead>
            <TableHead className={dateHeadClass}>Expiration</TableHead>
            <TableHead className={dateHeadClass}>Cancellation</TableHead>
            <TableHead className="w-8" aria-hidden="true" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {policies.map((policy) => {
            const cancellationReasonLabel = formatPolicyCancellationReason(policy);
            const rawCancellationReason = getRawCancellationReason(policy);
            const isNonPayment = policy.cancellationReason === "non_pay";
            const cancellationSummary =
              isNonPayment && !rawCancellationReason
                ? `${cancellationReasonLabel} · ${formatPolicyPremium(policy.amountDue)}`
                : cancellationReasonLabel;
            return (
            <TableRow
              key={policy.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onSelect(policy)}
              data-testid={`policy-row-${policy.policyNumber}`}
            >
              <TableCell className={`${cellClass} whitespace-nowrap font-semibold text-foreground`}>
                {policy.policyNumber}
              </TableCell>
              <TableCell className={`${cellClass} whitespace-nowrap text-muted-foreground`}>
                {policy.type || "—"}
              </TableCell>
              <TableCell className={cellClass}>
                <span className="inline-flex items-center gap-1.5">
                  <PolicyStatusBadge status={policy.status} />
                  {showAor && isAorFlagged(policy) && (
                    <AorIcon
                      tooltip={aorPolicyTooltip(policy.aorIdentifiedAt)}
                      testId={`policy-aor-${policy.policyNumber}`}
                    />
                  )}
                </span>
                <PolicySubstatusText substatus={policy.substatus} className="mt-0.5" />
              </TableCell>
              <TableCell className={`${cellClass} whitespace-nowrap text-muted-foreground`}>
                {policy.carrier}
              </TableCell>
              <TableCell className={`${cellClass} font-medium text-green-600`}>
                {formatPolicyPremium(policy.premium)}
              </TableCell>
              <TableCell className={dateCellClass}>
                {formatDate(policy.effectiveDate)}
              </TableCell>
              <TableCell className={dateCellClass}>
                {formatDate(policy.expirationDate)}
              </TableCell>
              <TableCell
                className={dateCellClass}
                data-testid={`policy-cancellation-${policy.policyNumber}`}
              >
                {shouldShowCancellationDate(policy.status) ? (
                  <div className="flex flex-col gap-0.5">
                    <span>
                      {policy.cancellationDate
                        ? formatDate(policy.cancellationDate)
                        : "—"}
                    </span>
                    {hasCancellationReason(policy) && (
                      <span
                        className="max-w-[9rem] truncate text-xs"
                        title={cancellationSummary}
                        data-testid={`policy-cancellation-reason-${policy.policyNumber}`}
                      >
                        {cancellationSummary}
                      </span>
                    )}
                    {isNonPayment && rawCancellationReason && (
                      <span
                        className="text-xs whitespace-nowrap"
                        data-testid={`policy-cancellation-amount-${policy.policyNumber}`}
                      >
                        {formatPolicyPremium(policy.amountDue)}
                      </span>
                    )}
                  </div>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell className={`${cellClass} w-8 pl-0 pr-6 text-muted-foreground`}>
                <ChevronRight className="w-4 h-4" aria-hidden="true" />
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
